import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  toast,
} from "@altimateai/lego";
import { ArrowDownIcon } from "@/assets/icons";
import { useAppState } from "@/modules/app/useAppContext";
import { ExplorerTracking } from "@/lib/tracking/explorer";
import { STATUS_ASSIGNMENT_OPTIONS } from "../constants";
import { useOpportunities } from "../useOpportunity";

interface StatusPickerProps {
  opportunity_id?: string;
  assignedStatus?: string | null;
  setAssignedStatus: (status: string) => void;
  startDate?: string;
  endDate?: string;
}

// Status colours from the "Discover (A)" design (data.js STATUS_COLORS).
const STATUS_COLORS: Record<string, { dot: string; bg: string; fg: string; border: string }> = {
  active: { bg: "#EFF4FF", fg: "#1849A9", border: "#B2CCFF", dot: "#2970FF" },
  implemented: { bg: "#ECFDF3", fg: "#067647", border: "#ABEFC6", dot: "#17B26A" },
  dismissed: { bg: "#F2F4F7", fg: "#475467", border: "#E4E7EC", dot: "#98A2B3" },
};

/**
 * Status picker for the opportunity detail page — renders the design's
 * coloured pill trigger + dot menu while reusing the exact mutation
 * (`useOpportunities`) and tracking that `StatusAssignerDropdown` uses, so
 * behaviour is unchanged. Discover-detail-scoped; the shared dropdown stays
 * as-is for the table.
 */
const StatusPicker = ({
  opportunity_id,
  assignedStatus,
  setAssignedStatus,
  startDate,
  endDate,
}: StatusPickerProps) => {
  const { mutation } = useOpportunities();
  const { mutate } = mutation;
  const { loggedinUser: user } = useAppState();

  const current = assignedStatus || "active";
  const c = STATUS_COLORS[current] || STATUS_COLORS.active;

  const handleChange = (status: string) => {
    if (!user?.id) {
      toast({ title: "Error", description: "Please try again later.", variant: "destructive" });
      console.error("User ID not found");
      return;
    }
    setAssignedStatus(status);
    ExplorerTracking.opportunityStatusAssigned(opportunity_id ?? "", status);
    mutate({
      changes: [{ opportunity_id: opportunity_id ?? "", status, owner: undefined }],
      user_id: user.id,
      start_date: startDate ?? "",
      end_date: endDate ?? "",
    });
  };

  const label = STATUS_ASSIGNMENT_OPTIONS.find(o => o.value === current)?.label ?? "Active";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={`Status: ${label}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            padding: "5px 9px 5px 11px",
            borderRadius: 8,
            border: `1px solid ${c.border}`,
            background: c.bg,
            color: c.fg,
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: 999, background: c.dot }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: c.fg }}>{label}</span>
          <ArrowDownIcon width={14} height={14} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="al-min-w-[168px]">
        {STATUS_ASSIGNMENT_OPTIONS.map(opt => {
          const oc = STATUS_COLORS[opt.value] || STATUS_COLORS.active;
          return (
            <DropdownMenuItem key={opt.value} onSelect={() => handleChange(opt.value)}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: oc.dot,
                  marginRight: 9,
                }}
              />
              {opt.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default StatusPicker;
